import { useMutation, useQuery } from '@apollo/client';
import { SettingsGroup, SettingsItemDate, SettingsItemNavigation } from '@elipssolution/harfang';
import { useCallback, useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';

import Banks from './banks/Banks';
import BillExchangeCounterpartAccount from './billExchangeCounterpartAccount/BillExchangeCounterpartAccount';
import CheckAccountTypes from './checkAccountTypes/CheckAccountTypes';
import CheckRemittanceCounterpartAccount from './checkRemittanceCounterpartAccount/CheckRemittanceCounterpartAccount';
import JournalCodes from './journalCodes/JournalCodes';
import PaymentModes from './paymentModes/PaymentModes';
import Templates from './templates/Templates';
import SettingsDialogPage from '../../../../../components/SettingsDialogPage';
import { useSettingsDialog } from '../../../../../hooks/useSettingsDialog';
import {
	FetchCustomerFileType,
	FETCH_CUSTOMER_FILE,
	QUICKENTRY_UPDATE_CUSTOMER_FILE_MINIMUM_ENTRY_DATE,
	QuickentryUpdateCustomerFileMinimumEntryDateType,
} from '../../../../../src/api/customerFile';
import { CustomerFileType } from '../../../../../types/customerFile';
import { CheckTypeEnum } from '../../../types/check';

type FormFieldsType = {
	minimumEntryDate?: Date;
};

type CustomerFileSectionProps = {
	customerFileId: CustomerFileType['id'];
};

const CustomerFileSection = ({ customerFileId }: CustomerFileSectionProps) => {
	const { push } = useSettingsDialog();

	const {
		control,
		formState: { isValid },
		handleSubmit,
		setValue,
		setError,
		watch,
	} = useForm<FormFieldsType>();

	const formMinimumEntryDate = watch('minimumEntryDate');

	const { data: customerFileFetchData } = useQuery<FetchCustomerFileType>(FETCH_CUSTOMER_FILE, {
		onCompleted: ({ customerFile: { minimumEntryDate } }) =>
			!!minimumEntryDate && setValue('minimumEntryDate', new Date(minimumEntryDate)),
		variables: {
			id: customerFileId,
		},
	});

	const { customerFile } = customerFileFetchData ?? {};
	const { accountingConnectorCode, name: customerFileName } = customerFile ?? {};

	const [updateCustomerFileMinimumEntryDate] = useMutation<QuickentryUpdateCustomerFileMinimumEntryDateType>(
		QUICKENTRY_UPDATE_CUSTOMER_FILE_MINIMUM_ENTRY_DATE,
	);

	const navigateToTemplatesSection = () =>
		push(<Templates accountingConnectorCode={accountingConnectorCode} customerFileId={customerFileId} />);

	const navigateToJournalCodesSection = () => push(<JournalCodes customerFileId={customerFileId} />);
	const navigateToPaymentModesSection = () => push(<PaymentModes customerFileId={customerFileId} />);
	const navigateToCheckIsssuanceAccountTypesSection = () =>
		push(<CheckAccountTypes checkType={CheckTypeEnum.CHECK_ISSUANCE} customerFileId={customerFileId} />);
	const navigateToCheckRemittanceAccountTypesSection = () =>
		push(<CheckAccountTypes customerFileId={customerFileId} checkType={CheckTypeEnum.CHECK_REMITTANCE} />);
	const navigateToCheckRemittanceCounterpartAccountSection = () =>
		push(<CheckRemittanceCounterpartAccount customerFileId={customerFileId} />);
	const navigateToBillExchangeCounterparAccountSection = () =>
		push(<BillExchangeCounterpartAccount customerFileId={customerFileId} />);
	const navigateToBanksSection = () => push(<Banks customerFileId={customerFileId} />);

	const onSubmit = useCallback(
		({ minimumEntryDate }: FormFieldsType) => {
			updateCustomerFileMinimumEntryDate({
				variables: {
					quickentry_updateCustomerFileMinimumEntryDateInput: {
						id: customerFileId,
						minimumEntryDate: minimumEntryDate ?? null,
					},
				},
			}).catch((error: Error) => setError('minimumEntryDate', error));
		},
		[setError, updateCustomerFileMinimumEntryDate, customerFileId],
	);

	useEffect(() => {
		if (isValid) {
			handleSubmit(onSubmit)().catch((error) => {
				throw error;
			});
		}
	}, [handleSubmit, onSubmit, isValid, formMinimumEntryDate]);

	return (
		<SettingsDialogPage title={customerFileName}>
			<Controller
				control={control}
				name="minimumEntryDate"
				render={({ field, fieldState: { error: fieldError } }) => (
					<SettingsItemDate
						{...field}
						label="Date minimale de saisie"
						invalid={!!fieldError}
						helperText={fieldError?.message}
					/>
				)}
			/>
			<SettingsGroup label="Paramétrage Modèles de pièce ">
				<SettingsItemNavigation label="Modèles de pièces" onClick={navigateToTemplatesSection} />
			</SettingsGroup>

			<SettingsGroup label="Paramétrage Saisie chèque, remise de chèque et LCR">
				<SettingsItemNavigation label="Journaux" onClick={navigateToJournalCodesSection} />
				<SettingsItemNavigation label="Modes de règlement" onClick={navigateToPaymentModesSection} />
				<SettingsItemNavigation label='Type "Chèque émis"' onClick={navigateToCheckIsssuanceAccountTypesSection} />
				<SettingsItemNavigation
					label='Type "Remise de chèque"'
					onClick={navigateToCheckRemittanceAccountTypesSection}
				/>
				<SettingsItemNavigation
					label="Compte de contrepartie de remise de chèques"
					onClick={navigateToCheckRemittanceCounterpartAccountSection}
				/>
				<SettingsItemNavigation
					label="Compte de contrepartie LCR émises"
					onClick={navigateToBillExchangeCounterparAccountSection}
				/>
				<SettingsItemNavigation label="Banques" onClick={navigateToBanksSection} />
			</SettingsGroup>
		</SettingsDialogPage>
	);
};

export default CustomerFileSection;
